import { useCallback, useMemo } from "react";

import {
  RenewalWithCheckReceivalFragment,
  useEditCheckReceivalDescriptionMutation,
  useGetPolicyWithCheckReceivalDataLazyQuery,
  useMarkRenewalAsPaidMutation,
} from "@covertree/agent-portal-graphql";
import { MarkRenewalAsPaid } from "@components/MarkRenewalAsPaidModal/validation";
import { Toast } from "@covertree/utils";
import { getErrorMessageFromApolloError } from "@utils/getErrorMessageFromApolloError";

export function useMarkRenewalAsPaid(
  renewal: RenewalWithCheckReceivalFragment,
  onClose: () => void
) {
  const [markRenewalAsPaid, { loading: isMarkAsPaidLoading }] =
    useMarkRenewalAsPaidMutation();

  const [editInvoiceSettlementDescription, { loading: isEditDescLoading }] =
    useEditCheckReceivalDescriptionMutation();

  const [getPolicy, { loading: isPolicyRefreshing }] =
    useGetPolicyWithCheckReceivalDataLazyQuery();

  const loading = useMemo(
    () => isMarkAsPaidLoading || isEditDescLoading || isPolicyRefreshing,
    [isEditDescLoading, isMarkAsPaidLoading, isPolicyRefreshing]
  );

  const handleSubmit = useCallback(
    async ({ description }: MarkRenewalAsPaid) => {
      if (!renewal?.renewalLocator) return;

      try {
        if (!!renewal.checkReceival) {
          await editInvoiceSettlementDescription({
            variables: { renewalLocator: renewal.renewalLocator, description },
          });
        } else {
          await markRenewalAsPaid({
            variables: { renewalLocator: renewal.renewalLocator, description },
          });
        }
        onClose();
        Toast.showSuccess("Operation was performed successfully!");
      } catch (e) {
        if (e.graphQLErrors[0].errorType === "PaymentRetrialError") {
          await getPolicy({
            variables: { policyLocator: renewal.policyLocator },
            fetchPolicy: "cache-and-network",
          });
          onClose();
        }
        Toast.showError(
          getErrorMessageFromApolloError(e) ||
            "Could not mark renewal as paid. Please try again"
        );
      }
    },
    [renewal]
  );

  return {
    handleSubmit,
    loading,
  };
}
